/* ==========================================================================
   OFFMARKET — scroll reveal
   --------------------------------------------------------------------------
   CSS scroll-driven animations (animation-timeline: view()) only ship in
   Chromium, so this uses IntersectionObserver instead and works everywhere.

   Elements are tagged, not hand-authored: the selectors below cover both the
   custom sections and Ritual's own components, so product grids on collection
   pages get the same treatment as the homepage without touching that markup.

   Content is visible by default. The reveal class only ever *removes* an
   offset, so if the script fails or is blocked nothing is ever hidden.
   ========================================================================== */

(() => {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduced.matches) return;
  if (!('IntersectionObserver' in window)) return;

  // [selector, stagger step in ms] — stagger only where items form a row/grid
  const TARGETS = [
    ['.om-index__head', 0],
    ['.om-rec', 45],
    ['.om-statement__body', 0],
    ['.om-spec', 90],
    ['.product-grid__card', 45],
    ['.product-card', 45],
    ['.collection-card', 60],
    ['.resource-card', 45],
    ['.shopify-section [class*="ai-promo-banner-content-"]', 0],
    ['.featured-product', 0],
  ];

  const seen = new WeakSet();

  /* Never tag anything that can sit outside the viewport without ever being
     scrolled into it vertically. Two classes of these, both verified failing:

       overlays   modals, drawers and predictive search are zero-size until
                  opened, so they never intersect — 8 search result cards were
                  stuck at opacity 0 and would have rendered invisible
       carousels  slides parked off to the right never intersect either, so a
                  shopper clicking the next arrow got a blank card

     Both stay untouched rather than being revealed on a timer, because an
     element this layer never hides is an element it can never break. */
  const EXCLUDE = [
    'dialog',
    '[role="dialog"]',
    '.dialog-modal',
    '.search-modal',
    '.quick-add-modal',
    '.cart-drawer',
    '.predictive-search-results',
    '.header',
    '.om-hero',
    'slideshow-slides',
    'slideshow-container',
    '.resource-list__carousel',
    '.resource-list__slide',
    '[class*="carousel"]',
  ].join(',');

  const prep = () => {
    TARGETS.forEach(([sel, step]) => {
      let i = 0;
      document.querySelectorAll(sel).forEach((el) => {
        if (seen.has(el)) return;
        if (el.closest(EXCLUDE)) return;

        // not laid out yet — leave it alone entirely so it can never be hidden
        if (!el.offsetParent && getComputedStyle(el).position !== 'fixed') return;
        const r = el.getBoundingClientRect();
        if (r.width === 0 || r.height === 0) return;

        seen.add(el);
        el.classList.add('om-reveal');
        if (step) {
          // restart the stagger per grid so long lists do not accumulate delay
          el.style.setProperty('--om-stagger', `${(i % 8) * step}ms`);
          i += 1;
        }
        observer.observe(el);
      });
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('om-reveal--in');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.06 }
  );

  const start = () => {
    prep();
    // sections stream in on collection pages and via the theme editor
    new MutationObserver(() => prep()).observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', prep);
})();
